import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Mail, Phone, MapPin, ExternalLink } from 'lucide-react'
import broxelLogo from '../../assets/images/BROXEL-LOGO.png'
import { USEFUL_LINKS } from '../../lib/externalLinks'
import SocialMediaLinks from '../ui/SocialMediaLinks'

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language as 'ar' | 'en'
  
  return (
    <footer className="bg-gradient-to-r from-muted/30 to-muted/60 border-t border-border/40">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center space-x-3 group">
              <img 
                src={broxelLogo} 
                alt="Broxel Pharmaceuticals" 
                className="h-12 w-auto transition-opacity group-hover:opacity-80"
              />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              {t('footer.description')}
            </p>
            <SocialMediaLinks variant="default" size="md" className="pt-2" wechat="modal" />
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-6 text-lg relative">
              {t('footer.quickLinks')}
              <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-primary rounded-full"></div>
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/" 
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('footer.services')}
                </Link>
              </li>
              <li>
                <Link
                  to="/eqs"
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('footer.eqs')}
                </Link>
              </li>
              <li>
                <Link
                  to="/partners"
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('footer.partners')}
                </Link>
              </li>
              {/* <li>
                <Link
                  to="/marketplace"
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('footer.equipment')}
                </Link>
              </li> */}
              <li>
                <Link
                  to="/about"
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('footer.about')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('footer.contact')}
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Useful Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-6 text-lg relative">
              {t('footer.usefulLinks')}
              <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-primary rounded-full"></div>
            </h3>
            <ul className="space-y-2">
              {USEFUL_LINKS[currentLanguage].map((link, index) => (
                <li key={index}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-flex items-center gap-1.5 group"
                  >
                    <span>{link.name}</span>
                    <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-foreground mb-6 text-lg relative">
              {t('footer.contactInfo')}
              <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-primary rounded-full"></div>
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 group">
                <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  Shrouk City Cairo, Egypt
                </span>
              </li>
              <li className="flex items-center space-x-3 group">
                <Phone className="h-5 w-5 text-primary shrink-0" />
                <a 
                  href="tel:+201118321999"
                  className="text-sm text-muted-foreground group-hover:text-primary transition-colors"
                >
                  +20 111 832 1999
                </a>
              </li>
              <li className="flex items-center space-x-3 group">
                <Mail className="h-5 w-5 text-primary shrink-0" />
                <a 
                  href="mailto:info@broxel-eg.com "
                  className="text-sm text-muted-foreground group-hover:text-primary transition-colors"
                >
                  info@broxel-eg.com 
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-border/40 mt-16 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-muted-foreground">
              © 2026 Broxel Pharmaceuticals. {t('footer.rights')}.
            </p>
            <div className="flex space-x-8 mt-4 md:mt-0">
              <Link
                to="/terms#privacy"
                className="text-sm text-muted-foreground hover:text-primary transition-colors relative group"
              >
                {t('footer.privacyPolicy')}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link
                to="/terms"
                className="text-sm text-muted-foreground hover:text-primary transition-colors relative group"
              >
                {t('footer.termsOfService')}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
